import {
  INBOX_CHAT_RIGHT_PANEL_MAX_WIDTH,
  INBOX_CHAT_RIGHT_PANEL_MIN_WIDTH,
  INBOX_CHAT_RIGHT_PANEL_OPEN_STORAGE_KEY,
  INBOX_CHAT_RIGHT_PANEL_WIDTH,
  INBOX_CHAT_RIGHT_PANEL_WIDTH_STORAGE_KEY,
  INBOX_CHAT_SIDEBAR_MAX_WIDTH,
  INBOX_CHAT_SIDEBAR_WIDTH,
  INBOX_CHAT_SIDEBAR_WIDTH_STORAGE_KEY,
  INBOX_PRIMARY_SIDEBAR_MAX_WIDTH,
  INBOX_PRIMARY_SIDEBAR_MIN_WIDTH,
  INBOX_PRIMARY_SIDEBAR_WIDTH,
  INBOX_PRIMARY_SIDEBAR_WIDTH_STORAGE_KEY,
} from '@/lib/inbox-v2/layout-constants'
import {
  SECTION_SIDEBAR_MAX_WIDTH,
  SECTION_SIDEBAR_WIDTH,
  SECTION_SIDEBAR_WIDTH_STORAGE_KEY,
} from './section-chrome-constants'

/**
 * Widths the operator can resize and that persist in localStorage.
 *
 * Skeletons render before React can read localStorage: during SSR and the
 * hydration pass they must match the server HTML. The init script below runs
 * in <head> before first paint and publishes every persisted width as a CSS
 * variable on <html>, so a skeleton styled with
 * `persistedLayoutWidthCssValue()` paints at the operator's width from the
 * first frame while its markup stays identical on server and client.
 */
export interface PersistedLayoutWidth {
  cssVariable: `--${string}`
  defaultWidth: number
  maxWidth: number
  minWidth: number
  storageKey: string
  /** Viewport width kept free for the main column (SectionChrome sidebars). */
  viewportReserve?: number
}

/** SectionChrome never lets a sidebar leave less than this for the content. */
export const SECTION_SIDEBAR_VIEWPORT_RESERVE_PX = 360

export const SECTION_SIDEBAR_PERSISTED_WIDTH: PersistedLayoutWidth = {
  cssVariable: '--trellis-section-sidebar-width',
  defaultWidth: SECTION_SIDEBAR_WIDTH,
  maxWidth: SECTION_SIDEBAR_MAX_WIDTH,
  minWidth: SECTION_SIDEBAR_WIDTH,
  storageKey: SECTION_SIDEBAR_WIDTH_STORAGE_KEY,
  viewportReserve: SECTION_SIDEBAR_VIEWPORT_RESERVE_PX,
}

export const RESERVATIONS_SIDEBAR_WIDTH_STORAGE_KEY = 'reservations-sidebar-width'

export const RESERVATIONS_SIDEBAR_PERSISTED_WIDTH: PersistedLayoutWidth = {
  ...SECTION_SIDEBAR_PERSISTED_WIDTH,
  cssVariable: '--trellis-reservations-sidebar-width',
  storageKey: RESERVATIONS_SIDEBAR_WIDTH_STORAGE_KEY,
}

export const INBOX_CHAT_SIDEBAR_PERSISTED_WIDTH: PersistedLayoutWidth = {
  cssVariable: '--trellis-inbox-chat-sidebar-width',
  defaultWidth: INBOX_CHAT_SIDEBAR_WIDTH,
  maxWidth: INBOX_CHAT_SIDEBAR_MAX_WIDTH,
  minWidth: INBOX_CHAT_SIDEBAR_WIDTH,
  storageKey: INBOX_CHAT_SIDEBAR_WIDTH_STORAGE_KEY,
  viewportReserve: SECTION_SIDEBAR_VIEWPORT_RESERVE_PX,
}

export const INBOX_PRIMARY_SIDEBAR_PERSISTED_WIDTH: PersistedLayoutWidth = {
  cssVariable: '--trellis-inbox-primary-sidebar-width',
  defaultWidth: INBOX_PRIMARY_SIDEBAR_WIDTH,
  maxWidth: INBOX_PRIMARY_SIDEBAR_MAX_WIDTH,
  minWidth: INBOX_PRIMARY_SIDEBAR_MIN_WIDTH,
  storageKey: INBOX_PRIMARY_SIDEBAR_WIDTH_STORAGE_KEY,
  viewportReserve: SECTION_SIDEBAR_VIEWPORT_RESERVE_PX,
}

export const INBOX_CHAT_RIGHT_PANEL_PERSISTED_WIDTH: PersistedLayoutWidth = {
  cssVariable: '--trellis-inbox-chat-right-panel-width',
  defaultWidth: INBOX_CHAT_RIGHT_PANEL_WIDTH,
  maxWidth: INBOX_CHAT_RIGHT_PANEL_MAX_WIDTH,
  minWidth: INBOX_CHAT_RIGHT_PANEL_MIN_WIDTH,
  storageKey: INBOX_CHAT_RIGHT_PANEL_WIDTH_STORAGE_KEY,
}

export const PERSISTED_LAYOUT_WIDTHS: readonly PersistedLayoutWidth[] = [
  SECTION_SIDEBAR_PERSISTED_WIDTH,
  RESERVATIONS_SIDEBAR_PERSISTED_WIDTH,
  INBOX_CHAT_SIDEBAR_PERSISTED_WIDTH,
  INBOX_PRIMARY_SIDEBAR_PERSISTED_WIDTH,
  INBOX_CHAT_RIGHT_PANEL_PERSISTED_WIDTH,
]

/**
 * Sidebar width of the route being loaded, for the static shell that paints
 * before the route is known to React.
 */
export const ROUTE_SIDEBAR_WIDTH_CSS_VARIABLE = '--trellis-route-sidebar-width'

/**
 * `closed` on <html> when the operator closed the inbox chat details panel.
 * Distinct from the panel's own `data-inbox-chat-right-panel` marker so
 * selectors for the panel never match <html>.
 */
export const INBOX_CHAT_RIGHT_PANEL_STATE_ATTRIBUTE = 'data-inbox-chat-details-panel-preference'

export function findPersistedLayoutWidth(storageKey: string): PersistedLayoutWidth | undefined {
  return PERSISTED_LAYOUT_WIDTHS.find((entry) => entry.storageKey === storageKey)
}

/** Width for first paint: the init script fills the variable before hydration. */
export function persistedLayoutWidthCssValue(entry: PersistedLayoutWidth): string {
  return `var(${entry.cssVariable}, ${entry.defaultWidth}px)`
}

export function routeSidebarWidthCssValue(): string {
  return `var(${ROUTE_SIDEBAR_WIDTH_CSS_VARIABLE}, ${SECTION_SIDEBAR_WIDTH}px)`
}

export function clampPersistedLayoutWidth(
  entry: PersistedLayoutWidth,
  width: number,
  viewportWidth?: number
): number {
  const viewportMax =
    entry.viewportReserve === undefined || viewportWidth === undefined
      ? entry.maxWidth
      : viewportWidth - entry.viewportReserve
  return Math.max(entry.minWidth, Math.min(entry.maxWidth, viewportMax, width))
}

function browserStorage(): Pick<Storage, 'getItem'> | null {
  if (typeof window === 'undefined') {
    return null
  }
  try {
    return window.localStorage
  } catch {
    return null
  }
}

/** The persisted width, clamped the same way the resizable surface clamps it. */
export function readPersistedLayoutWidth(
  entry: PersistedLayoutWidth,
  {
    storage = browserStorage(),
    viewportWidth = typeof window === 'undefined' ? undefined : window.innerWidth,
  }: {
    storage?: Pick<Storage, 'getItem'> | null
    viewportWidth?: number
  } = {}
): number {
  let saved = Number.NaN
  try {
    const raw = storage?.getItem(entry.storageKey)
    saved = raw ? Number.parseInt(raw, 10) : Number.NaN
  } catch {
    // Storage unavailable: fall back to the default width.
  }
  return clampPersistedLayoutWidth(
    entry,
    Number.isFinite(saved) ? saved : entry.defaultWidth,
    viewportWidth
  )
}

/** Persist a resized width and keep the first-paint variable in sync. */
export function writePersistedLayoutWidth(entry: PersistedLayoutWidth, width: number): void {
  try {
    window.localStorage.setItem(entry.storageKey, String(width))
  } catch {
    // localStorage unavailable — the width still applies for this session.
  }
  document.documentElement.style.setProperty(entry.cssVariable, `${width}px`)
}

/** Keep the first-paint details-panel state in sync with the stored preference. */
export function syncInboxChatRightPanelStateAttribute(open: boolean): void {
  if (open) {
    document.documentElement.removeAttribute(INBOX_CHAT_RIGHT_PANEL_STATE_ATTRIBUTE)
  } else {
    document.documentElement.setAttribute(INBOX_CHAT_RIGHT_PANEL_STATE_ATTRIBUTE, 'closed')
  }
}

const scriptEntries = PERSISTED_LAYOUT_WIDTHS.map((entry) => [
  entry.storageKey,
  entry.cssVariable,
  entry.defaultWidth,
  entry.minWidth,
  entry.maxWidth,
  entry.viewportReserve ?? null,
])

/**
 * Runs in <head> before first paint. Mirrors `readPersistedLayoutWidth` and
 * the sidebar choice of `getWorkspaceShellLoadingKind` for the static shell
 * (inbox chat, inbox list, reservations, every other section; none on the
 * workspace calendar).
 */
export const PERSISTED_LAYOUT_INIT_SCRIPT = `(function(){try{var d=document.documentElement,s=d.style,v=window.innerWidth,e=${JSON.stringify(
  scriptEntries
)},w={},i,x,r,n,m;for(i=0;i<e.length;i++){x=e[i];r=null;try{r=localStorage.getItem(x[0])}catch(_){}n=r?parseInt(r,10):NaN;if(!isFinite(n))n=x[2];m=x[5]===null?x[4]:Math.min(x[4],v-x[5]);n=Math.max(x[3],Math.min(m,n));w[x[0]]=n;s.setProperty(x[1],n+"px")}var p=location.pathname,g=p.split("/").filter(Boolean),k=${JSON.stringify(
  SECTION_SIDEBAR_WIDTH_STORAGE_KEY
)};if(/\\/inbox-v2\\/(?:[^/]+\\/)?chat(?:\\/|$)/.test(p))k=${JSON.stringify(
  INBOX_CHAT_SIDEBAR_WIDTH_STORAGE_KEY
)};else if((x=g.indexOf("inbox-v2"))>=0&&g[x+1])k=${JSON.stringify(
  INBOX_PRIMARY_SIDEBAR_WIDTH_STORAGE_KEY
)};else if(g[1]==="reservations")k=${JSON.stringify(
  RESERVATIONS_SIDEBAR_WIDTH_STORAGE_KEY
)};s.setProperty(${JSON.stringify(ROUTE_SIDEBAR_WIDTH_CSS_VARIABLE)},g[1]==="calendar"?"0px":w[k]+"px");r=null;try{r=localStorage.getItem(${JSON.stringify(
  INBOX_CHAT_RIGHT_PANEL_OPEN_STORAGE_KEY
)})}catch(_){}if(r!=="true")d.setAttribute(${JSON.stringify(
  INBOX_CHAT_RIGHT_PANEL_STATE_ATTRIBUTE
)},"closed")}catch(_){}})()`
